import type { FavoriteKey } from "@screener/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FavoritesState } from "./favorite-match";
import {
  addFavorite,
  type FavoriteAddResult,
  type FavoriteRemoveResult,
  type FavoritesListResult,
  fetchFavorites,
  removeFavorite,
} from "./favorites-api";

/**
 * Тип живёт в `favorite-match.ts` — чистом модуле без React (review
 * stage6-favorites, п. 6): `derive.ts`/`situation-derive.ts` берут его
 * оттуда напрямую, а хук здесь только реэкспортирует то же имя для своих
 * потребителей.
 */
export type { FavoritesState };

interface UseFavoritesOptions {
  onSessionInvalid: () => void;
}

interface UseFavoritesResult {
  state: FavoritesState;
  /**
   * Поставить или снять избранное монеты целиком — с пустыми биржей и
   * рынком (спека `user-favorites`, design.md решение 6): §8 и §9 говорят
   * об избранных монетах, а не парах или уровнях.
   */
  toggle: (base: string, currentlyFavorite: boolean) => Promise<"ok" | "invalid" | "unavailable">;
  /**
   * Монеты, чьё собственное действие избранного ещё не завершилось —
   * повторное нажатие до ответа игнорируется (design.md решение 5, тот же
   * приём, что `pendingPinIds`).
   */
  pendingBases: ReadonlySet<string>;
  /**
   * Перечитать список вручную — тот же `load`, что и при входе (review
   * stage6-favorites, второй круг, п. 1): нажатие звёздочки при непрочитанном
   * списке вызывает именно это, а не запись.
   */
  reload: () => Promise<void>;
}

/**
 * Состояние перед перечитыванием списка — тот же приём, что у заметок и
 * закреплений (`beforeNotesReload`/`beforePinsReload`): уже прочитанный
 * список остаётся видимым во время `GET`, вызванного собственным действием.
 */
export function beforeFavoritesReload(previous: FavoritesState): FavoritesState {
  return previous.status === "ready" ? previous : { status: "loading" };
}

/**
 * Единственная последовательность запроса действия избранного: запись
 * (add/remove), и на успехе — перечитывание списка. Вынесено чистой функцией
 * ради теста гонки (review stage6-favorites, п. 1): монета обязана
 * оставаться «в пути», пока перечитывание не завершилось, а не только пока не
 * ответил `POST`/`DELETE` — иначе второй клик после ответа записи, но до
 * ответа перечитывания, ушёл бы вторым запросом поверх ещё не осевшего
 * первого. Зависимости переданы параметром, а не импортированы внутри: тест
 * ловит саму последовательность, без сети и без React.
 */
export async function sendFavoriteToggle(
  base: string,
  currentlyFavorite: boolean,
  deps: {
    add: (key: FavoriteKey) => Promise<FavoriteAddResult>;
    remove: (key: FavoriteKey) => Promise<FavoriteRemoveResult>;
    reload: () => Promise<void>;
  },
): Promise<"ok" | "invalid" | "unavailable" | "unauthorized"> {
  const key: FavoriteKey = { exchange: null, market: null, base };
  const result = currentlyFavorite ? await deps.remove(key) : await deps.add(key);
  if (result.status === "unauthorized") {
    return "unauthorized";
  }
  if (result.status === "ok") {
    await deps.reload();
  }
  return result.status;
}

/**
 * Занятость монеты держится ровно этим множеством — не его копией (review
 * stage6-favorites, второй круг, п. 6): хук и тест гонки пользуются одной и
 * той же функцией, и перенос снятия занятости до `await` внутри
 * `sendFavoriteToggle` обязан ронять тест, а не только рантайм. Второй вызов
 * той же монеты, пока первый ещё в пути (запись плюс её перечитывание,
 * `sendFavoriteToggle`), возвращает `null` — второй запрос не отправляется.
 */
export function guardedFavoriteToggle(
  pending: Set<string>,
  base: string,
  currentlyFavorite: boolean,
  deps: {
    add: (key: FavoriteKey) => Promise<FavoriteAddResult>;
    remove: (key: FavoriteKey) => Promise<FavoriteRemoveResult>;
    reload: () => Promise<void>;
  },
): Promise<"ok" | "invalid" | "unavailable" | "unauthorized"> | null {
  if (pending.has(base)) {
    return null;
  }
  pending.add(base);
  return sendFavoriteToggle(base, currentlyFavorite, deps).finally(() => pending.delete(base));
}

/**
 * Перечитывание списка избранного с защитой от устаревшего ответа (review
 * stage6-favorites, второй круг, п. 4): каждый вызов несёт свой порядковый
 * номер, и ответ, чей номер меньше номера последнего отправленного вызова,
 * отбрасывается (`null`) — иначе результат более раннего, но опоздавшего
 * `GET` лёг бы поверх уже осевшего результата более позднего перечитывания.
 * Чистая фабрика вместо счётчика прямо в хуке — ради теста самой гонки, без
 * рендера React (тот же приём, что `sendFavoriteToggle`).
 */
export function createFavoritesReloader(
  fetch: () => Promise<FavoritesListResult>,
): () => Promise<FavoritesListResult | null> {
  let latestRequestId = 0;
  return async () => {
    latestRequestId += 1;
    const requestId = latestRequestId;
    const result = await fetch();
    return requestId === latestRequestId ? result : null;
  };
}

/**
 * Избранное §6.6 общим списком пользователя: загрузка при входе,
 * перезагрузка после собственного действия, без опроса — тот же приём, что
 * `usePins`/`useNotes`.
 */
export function useFavorites({ onSessionInvalid }: UseFavoritesOptions): UseFavoritesResult {
  const [state, setState] = useState<FavoritesState>({ status: "loading" });
  const [pendingBases, setPendingBases] = useState<ReadonlySet<string>>(new Set());
  // Занятость монеты — одно множество на весь хук, а не состояние React:
  // `guardedFavoriteToggle` проверяет и мутирует его синхронно, до первого
  // `await` (review stage6-favorites, второй круг, п. 6). `pendingBases`
  // ниже — снимок этого множества для рендера, снятый сразу после мутации.
  const pendingRef = useRef<Set<string>>(new Set());
  const reloadRef = useRef(createFavoritesReloader(fetchFavorites));

  // Возвращает свой промис: `toggle` обязан дождаться конца этого `GET`
  // (review stage6-favorites, п. 1), а не только запустить его.
  const load = useCallback((): Promise<void> => {
    setState(beforeFavoritesReload);
    return reloadRef.current().then((result) => {
      if (result === null) {
        // Устарел: более новое перечитывание уже отправлено или уже осело
        // (review stage6-favorites, второй круг, п. 4).
        return;
      }
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return;
      }
      setState(
        result.status === "ok"
          ? { status: "ready", favorites: result.favorites }
          : { status: "unavailable" },
      );
    });
  }, [onSessionInvalid]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback(
    async (base: string, currentlyFavorite: boolean): Promise<"ok" | "invalid" | "unavailable"> => {
      // Ожидание держится по монете, не по запросу: повторное нажатие до
      // ответа на предыдущий не имеет права превратить снятие в постановку
      // (спека `web-density-table` «Отказ записи избранного назван», тот же
      // приём, что `canSendPinToggle`). Снимается только после того, как
      // осело и само перечитывание (`sendFavoriteToggle` ждёт `load()`),
      // иначе второй клик мог уйти вторым запросом, пока список ещё не
      // отразил первый (review stage6-favorites, п. 1).
      const outcome = guardedFavoriteToggle(pendingRef.current, base, currentlyFavorite, {
        add: addFavorite,
        remove: removeFavorite,
        reload: load,
      });
      if (outcome === null) {
        // У монеты уже есть запрос в пути — вызывающая сторона это тоже
        // проверяет (`favoriteClickIntent`), это крайний случай той же гонки.
        return "unavailable";
      }
      setPendingBases(new Set(pendingRef.current));
      const result = await outcome;
      setPendingBases(new Set(pendingRef.current));
      if (result === "unauthorized") {
        onSessionInvalid();
        return "unavailable";
      }
      return result;
    },
    [load, onSessionInvalid],
  );

  return { state, toggle, pendingBases, reload: load };
}
